import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Form, Button, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { ServerPath } from '../../Constant';
import { _axios } from '../../utils/axios';
import { uploadImage } from '../../Services/r2-storage-blob.js';
import cookieUtils from '../../utils/cookieUtils';
import Search from '../../assets/images/svg/search.svg';
import {
    QUIZ_CATEGORY_OPTIONS,
    TOPIC_OPTIONS,
    STATUS_OPTIONS,
    ARTIFACT_TYPE_OPTIONS,
    TIMER_OPTIONS,
    OPTION_LETTERS,
    QUIZ_LANGUAGES,
    MAX_LEVELS,
    createEmptyQuestion,
    normalizeQuestionFromApi,
    buildQuestionPayload,
    validateQuestion,
    buildMappings,
    validateQuizDetails,
    getApiErrorMessage,
    isConflictError,
    isNotFoundError
} from './QuizManagementData';

const labelStyle = { color: '#4A5568', fontWeight: '500' };
const controlStyle = { border: '1px solid #E2E8F0', backgroundColor: '#FFFFFF' };
const selectStyle = { ...controlStyle, height: '44px' };

const initialFormState = {
    title: '',
    title_hin: '',
    title_mar: '',
    title_guj: '',
    description: '',
    description_hin: '',
    description_mar: '',
    description_guj: '',
    thumbnail_url: '',
    quiz_category: '',
    topic: '',
    artifact_type: '',
    timer_enabled: 0,
    time_per_question_seconds: '',
    shuffle_questions: 0,
    status: 'DRAFT'
};

/**
 * Shared quiz form used by both CreateQuizManagement and EditQuizManagement.
 * It renders the Modal.Body / Modal.Footer of the surrounding modal.
 *
 * mode = 'create' - everything is staged locally and sent with POST /quiz-admin
 * mode = 'edit'   - details are saved with PUT /quiz-admin/:quizId while each
 *                   question is saved individually against its own endpoint.
 */
const QuizForm = ({ mode, quizId, show, onClose }) => {
    const userRole = cookieUtils.getSecureCookie('role');
    const adminId = cookieUtils.getSecureCookie('admin_id');
    const userId = cookieUtils.getSecureCookie('user_id');
    const currentId = userRole === 'superadmin' ? adminId : userId;

    const isEdit = mode === 'edit';

    const [formData, setFormData] = useState(initialFormState);
    const [loading, setLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Thumbnail - a single image for the whole quiz (not per language).
    const [thumbnailFile, setThumbnailFile] = useState(null);
    const [thumbnailPreview, setThumbnailPreview] = useState('');

    // Artifact placement
    const [artifactOptions, setArtifactOptions] = useState([]);
    const [artifactSearch, setArtifactSearch] = useState('');
    const [selectedArtifacts, setSelectedArtifacts] = useState([]);

    // Course placement
    const [courses, setCourses] = useState([]);
    const [chapters, setChapters] = useState([]);
    const [selectedCourseId, setSelectedCourseId] = useState('');
    const [selectedChapterId, setSelectedChapterId] = useState('');

    // Questions across all levels
    const [questions, setQuestions] = useState([]);
    const [extraLevels, setExtraLevels] = useState([1]);
    const [activeLevel, setActiveLevel] = useState(1);
    const [savingQuestionKey, setSavingQuestionKey] = useState(null);

    const [mappingError, setMappingError] = useState('');
    const dragKeyRef = useRef(null);
    const [draggableKey, setDraggableKey] = useState(null);

    const isQuizSection = formData.quiz_category === 'QUIZ_SECTION';

    /* -------------------------------------------------------------- *
     * Loading
     * -------------------------------------------------------------- */

    const resetForm = () => {
        setFormData(initialFormState);
        setThumbnailFile(null);
        setThumbnailPreview('');
        setArtifactOptions([]);
        setArtifactSearch('');
        setSelectedArtifacts([]);
        setChapters([]);
        setSelectedCourseId('');
        setSelectedChapterId('');
        setQuestions([]);
        setExtraLevels([1]);
        setActiveLevel(1);
        setMappingError('');
    };

    useEffect(() => {
        if (!show) return;

        if (isEdit && quizId) {
            loadQuiz(quizId);
        } else if (!isEdit) {
            resetForm();
            setQuestions([createEmptyQuestion(1)]);
        }
    }, [show, quizId, isEdit]);

    const fetchCourses = async () => {
        try {
            const response = await _axios.get(`${ServerPath}/course/all`);
            if (response?.data?.success) {
                const list = response.data.data || [];
                setCourses(list);
                return list;
            }
        } catch (error) {
            console.error('Error fetching courses:', error);
            toast.error('Failed to load courses');
        }
        return [];
    };

    const fetchChapters = async (courseId) => {
        if (!courseId) {
            setChapters([]);
            return [];
        }
        try {
            const response = await _axios.get(`${ServerPath}/chapter/course/${courseId}`);
            if (response?.data?.success) {
                const list = response.data.data || [];
                setChapters(list);
                return list;
            }
        } catch (error) {
            console.error('Error fetching chapters:', error);
            toast.error('Failed to load chapters');
        }
        setChapters([]);
        return [];
    };

    const fetchArtifacts = async (artifactType) => {
        if (!artifactType) {
            setArtifactOptions([]);
            return [];
        }
        try {
            // The dropdown endpoints take the type in lowercase: read | listen | watch
            const response = await _axios.get(`${ServerPath}/artifacts/dropdown/${artifactType.toLowerCase()}`);
            if (response?.data?.success) {
                const list = response.data.data || [];
                setArtifactOptions(list);
                return list;
            }
        } catch (error) {
            console.error('Error fetching artifacts:', error);
            toast.error('Failed to load artifacts');
        }
        setArtifactOptions([]);
        return [];
    };

    // A CHAPTER mapping only carries the chapter id, so walk the courses to
    // find the one that owns it and preselect both dropdowns.
    const resolveChapterCourse = async (chapterId) => {
        const courseList = await fetchCourses();
        for (const course of courseList) {
            const chapterList = await fetchChapters(course.course_id);
            const match = chapterList.find(chapter => String(chapter.chapter_id) === String(chapterId));
            if (match) {
                return { courseId: course.course_id, chapterList };
            }
        }
        return null;
    };

    const loadQuiz = async (id) => {
        try {
            setLoading(true);
            setMappingError('');
            const response = await _axios.get(`${ServerPath}/quiz-admin/${id}`);

            if (!response?.data?.success) {
                toast.error(response?.data?.message || 'Failed to load quiz');
                return;
            }

            const quiz = response.data.data;

            setFormData({
                title: quiz.title || '',
                title_hin: quiz.title_hin || '',
                title_mar: quiz.title_mar || '',
                title_guj: quiz.title_guj || '',
                description: quiz.description || '',
                description_hin: quiz.description_hin || '',
                description_mar: quiz.description_mar || '',
                description_guj: quiz.description_guj || '',
                thumbnail_url: quiz.thumbnail_url || '',
                quiz_category: quiz.quiz_category || '',
                topic: quiz.topic || '',
                artifact_type: quiz.artifact_type || '',
                timer_enabled: quiz.timer_enabled ? 1 : 0,
                time_per_question_seconds: quiz.time_per_question_seconds || '',
                shuffle_questions: quiz.shuffle_questions ? 1 : 0,
                status: quiz.status || 'DRAFT'
            });
            setThumbnailFile(null);
            setThumbnailPreview(quiz.thumbnail_url || '');

            const loadedQuestions = (quiz.questions || []).map(normalizeQuestionFromApi);
            setQuestions(loadedQuestions);

            const usedLevels = [...new Set(loadedQuestions.map(question => question.level_number))].sort((a, b) => a - b);
            setExtraLevels(usedLevels.length ? usedLevels : [1]);
            setActiveLevel(usedLevels.length ? usedLevels[0] : 1);

            const mappings = quiz.mappings || [];

            if (quiz.quiz_category === 'ARTIFACT') {
                const artifactList = await fetchArtifacts(quiz.artifact_type);
                const mappedIds = mappings
                    .filter(mapping => mapping.entity_type === 'ARTIFACT')
                    .map(mapping => String(mapping.entity_id));
                const selected = mappedIds.map(mappedId => {
                    const match = artifactList.find(artifact => String(artifact.artifact_id) === mappedId);
                    return match || { artifact_id: mappedId, title: `Artifact ${mappedId}` };
                });
                setSelectedArtifacts(selected);
                setSelectedCourseId('');
                setSelectedChapterId('');
            } else if (quiz.quiz_category === 'COURSE') {
                setSelectedArtifacts([]);
                const mapping = mappings[0];
                if (mapping?.entity_type === 'CHAPTER') {
                    const resolved = await resolveChapterCourse(mapping.entity_id);
                    if (resolved) {
                        setSelectedCourseId(resolved.courseId);
                        setSelectedChapterId(mapping.entity_id);
                    } else {
                        toast.warn('The mapped chapter could not be found - please re-select it');
                        setSelectedCourseId('');
                        setSelectedChapterId('');
                    }
                } else if (mapping?.entity_type === 'COURSE') {
                    await fetchCourses();
                    await fetchChapters(mapping.entity_id);
                    setSelectedCourseId(mapping.entity_id);
                    setSelectedChapterId('');
                } else {
                    await fetchCourses();
                    setSelectedCourseId('');
                    setSelectedChapterId('');
                }
            } else {
                setSelectedArtifacts([]);
                setSelectedCourseId('');
                setSelectedChapterId('');
            }
        } catch (error) {
            console.error('Error loading quiz:', error);
            if (isNotFoundError(error)) {
                toast.error('This quiz no longer exists');
                onClose(true);
                return;
            }
            toast.error(getApiErrorMessage(error, 'Failed to load quiz'));
        } finally {
            setLoading(false);
        }
    };

    /* -------------------------------------------------------------- *
     * Details handlers
     * -------------------------------------------------------------- */

    const handleFieldChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleCategoryChange = async (value) => {
        setMappingError('');
        setFormData(prev => ({
            ...prev,
            quiz_category: value,
            // Topic is chosen independently of the category, so it survives here.
            artifact_type: value === 'ARTIFACT' ? prev.artifact_type : ''
        }));
        setSelectedArtifacts([]);
        setSelectedCourseId('');
        setSelectedChapterId('');
        setChapters([]);
        setArtifactOptions([]);
        setArtifactSearch('');

        if (value === 'COURSE') {
            await fetchCourses();
        }

        if (value !== 'QUIZ_SECTION') {
            // Artifact and Course quizzes have no levels - everything is level 1.
            setQuestions(prev => prev.map(question => ({
                ...question,
                level_number: 1,
                dirty: question.level_number === 1 ? question.dirty : true
            })));
            setExtraLevels([1]);
            setActiveLevel(1);
        }
    };

    const handleArtifactTypeChange = async (value) => {
        setMappingError('');
        handleFieldChange('artifact_type', value);
        setSelectedArtifacts([]);
        setArtifactSearch('');
        await fetchArtifacts(value);
    };

    const handleCourseChange = async (value) => {
        setMappingError('');
        setSelectedCourseId(value);
        setSelectedChapterId('');
        await fetchChapters(value);
    };

    const handleThumbnailChange = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (!['image/png', 'image/jpeg', 'image/jpg', 'image/webp'].includes(file.type)) {
            toast.error('Please upload the thumbnail in JPG, PNG or WEBP format');
            return;
        }

        setThumbnailFile(file);
        setThumbnailPreview(URL.createObjectURL(file));
    };

    const handleThumbnailRemove = (event) => {
        event.stopPropagation();
        setThumbnailFile(null);
        setThumbnailPreview('');
        handleFieldChange('thumbnail_url', '');
        const input = document.getElementById('quizThumbnailFile');
        if (input) input.value = '';
    };

    /* -------------------------------------------------------------- *
     * Levels
     * -------------------------------------------------------------- */

    const visibleLevels = useMemo(() => {
        const used = questions.map(question => question.level_number);
        return [...new Set([...extraLevels, ...used])].sort((a, b) => a - b);
    }, [extraLevels, questions]);

    const levelQuestions = useMemo(
        () => questions.filter(question => question.level_number === (isQuizSection ? activeLevel : 1)),
        [questions, activeLevel, isQuizSection]
    );

    const handleAddLevel = () => {
        const nextLevel = (visibleLevels[visibleLevels.length - 1] || 0) + 1;
        if (nextLevel > MAX_LEVELS) {
            toast.error(`A quiz can have at most ${MAX_LEVELS} levels`);
            return;
        }
        setExtraLevels(prev => [...new Set([...prev, nextLevel])].sort((a, b) => a - b));
        setActiveLevel(nextLevel);
        // A level only exists through its questions, so start it with one.
        setQuestions(prev => [...prev, createEmptyQuestion(nextLevel)]);
    };

    /* -------------------------------------------------------------- *
     * Questions
     * -------------------------------------------------------------- */

    const updateQuestion = (key, changes) => {
        setQuestions(prev => prev.map(question =>
            question.key === key ? { ...question, ...changes, dirty: true } : question
        ));
    };

    const updateQuestionOption = (key, suffix, index, value) => {
        setQuestions(prev => prev.map(question => {
            if (question.key !== key) return question;
            const field = `options${suffix}`;
            const options = [...question[field]];
            options[index] = value;
            return { ...question, [field]: options, dirty: true };
        }));
    };

    const handleAddQuestion = () => {
        setQuestions(prev => [...prev, createEmptyQuestion(isQuizSection ? activeLevel : 1)]);
    };

    const questionLabel = (question, index) =>
        isQuizSection ? `Level ${question.level_number} Question ${index + 1}` : `Question ${index + 1}`;

    const handleSaveQuestion = async (question, index) => {
        const error = validateQuestion(question, questionLabel(question, index));
        if (error) {
            toast.error(error);
            return;
        }

        // In create mode the quiz does not exist yet - questions go out with
        // the quiz itself, so saving here just marks it as complete.
        if (!isEdit) {
            setQuestions(prev => prev.map(item =>
                item.key === question.key ? { ...item, dirty: false } : item
            ));
            toast.success('Question saved');
            return;
        }

        try {
            setSavingQuestionKey(question.key);
            const payload = buildQuestionPayload(question);

            if (question.id) {
                const response = await _axios.put(`${ServerPath}/quiz-admin/questions/${question.id}`, payload);
                if (!response?.data?.success) {
                    toast.error(response?.data?.message || 'Failed to save question');
                    return;
                }
            } else {
                const response = await _axios.post(`${ServerPath}/quiz-admin/${quizId}/questions`, payload);
                if (!response?.data?.success) {
                    toast.error(response?.data?.message || 'Failed to save question');
                    return;
                }
                const newId = response.data.data?.id;
                setQuestions(prev => prev.map(item =>
                    item.key === question.key ? { ...item, id: newId } : item
                ));
            }

            setQuestions(prev => prev.map(item =>
                item.key === question.key ? { ...item, dirty: false } : item
            ));
            toast.success('Question saved');
        } catch (error) {
            console.error('Error saving question:', error);
            if (isNotFoundError(error)) {
                toast.error('This question was deleted elsewhere - reloading the quiz');
                loadQuiz(quizId);
                return;
            }
            toast.error(getApiErrorMessage(error, 'Failed to save question'));
        } finally {
            setSavingQuestionKey(null);
        }
    };

    const handleDeleteQuestion = async (question, index) => {
        if (!window.confirm(`Delete ${questionLabel(question, index)}? This cannot be undone.`)) {
            return;
        }

        if (!isEdit || !question.id) {
            setQuestions(prev => prev.filter(item => item.key !== question.key));
            return;
        }

        try {
            const response = await _axios.delete(`${ServerPath}/quiz-admin/questions/${question.id}`);
            if (response?.data?.success) {
                setQuestions(prev => prev.filter(item => item.key !== question.key));
                toast.success('Question deleted');
            } else {
                toast.error(response?.data?.message || 'Failed to delete question');
            }
        } catch (error) {
            console.error('Error deleting question:', error);
            if (isNotFoundError(error)) {
                setQuestions(prev => prev.filter(item => item.key !== question.key));
                return;
            }
            toast.error(getApiErrorMessage(error, 'Failed to delete question'));
        }
    };

    /* Drag and drop reordering, scoped to the level being displayed. */

    const handleDragStart = (key) => {
        dragKeyRef.current = key;
    };

    const handleDragOver = (event) => {
        event.preventDefault();
    };

    const handleDrop = async (targetKey) => {
        const sourceKey = dragKeyRef.current;
        dragKeyRef.current = null;
        setDraggableKey(null);

        if (!sourceKey || sourceKey === targetKey) return;

        const levelNumber = isQuizSection ? activeLevel : 1;
        const inLevel = questions.filter(question => question.level_number === levelNumber);
        const sourceIndex = inLevel.findIndex(question => question.key === sourceKey);
        const targetIndex = inLevel.findIndex(question => question.key === targetKey);
        if (sourceIndex === -1 || targetIndex === -1) return;

        const reordered = [...inLevel];
        const [moved] = reordered.splice(sourceIndex, 1);
        reordered.splice(targetIndex, 0, moved);

        // Splice the reordered level back into the full list, keeping the
        // other levels where they are.
        let cursor = 0;
        const nextQuestions = questions.map(question =>
            question.level_number === levelNumber ? reordered[cursor++] : question
        );
        setQuestions(nextQuestions);

        if (!isEdit) return;

        const savedQuestions = reordered.filter(question => question.id);
        if (savedQuestions.length < 2) return;

        try {
            const response = await _axios.put(`${ServerPath}/quiz-admin/${quizId}/questions/reorder`, {
                questions: savedQuestions.map((question, index) => ({
                    id: question.id,
                    sequence_number: index + 1
                }))
            });
            if (response?.data?.success) {
                toast.success('Question order updated');
            } else {
                toast.error(response?.data?.message || 'Failed to update the question order');
            }
        } catch (error) {
            console.error('Error reordering questions:', error);
            toast.error(getApiErrorMessage(error, 'Failed to update the question order'));
        }
    };

    /* -------------------------------------------------------------- *
     * Submit
     * -------------------------------------------------------------- */

    const buildDetailsPayload = (thumbnailUrl) => ({
        title: formData.title.trim(),
        title_hin: formData.title_hin.trim() || null,
        title_mar: formData.title_mar.trim() || null,
        title_guj: formData.title_guj.trim() || null,
        description: formData.description.trim() || null,
        description_hin: formData.description_hin.trim() || null,
        description_mar: formData.description_mar.trim() || null,
        description_guj: formData.description_guj.trim() || null,
        thumbnail_url: thumbnailUrl || null,
        quiz_category: formData.quiz_category,
        topic: formData.topic || null,
        artifact_type: formData.quiz_category === 'ARTIFACT' ? (formData.artifact_type || null) : null,
        timer_enabled: formData.timer_enabled ? 1 : 0,
        time_per_question_seconds: formData.timer_enabled ? Number(formData.time_per_question_seconds) : null,
        shuffle_questions: formData.shuffle_questions ? 1 : 0,
        status: formData.status
    });

    const handleSubmit = async () => {
        if (isSubmitting) return;

        setMappingError('');

        const detailsError = validateQuizDetails(formData, {
            selectedArtifacts,
            courseId: selectedCourseId
        });
        if (detailsError) {
            toast.error(detailsError);
            return;
        }

        const mappings = buildMappings(formData.quiz_category, {
            selectedArtifacts,
            courseId: selectedCourseId,
            chapterId: selectedChapterId
        });

        // Guard the placement rules the server enforces.
        if (formData.quiz_category === 'QUIZ_SECTION' && mappings.length > 0) {
            toast.error('A Quiz Section quiz cannot be mapped to any artifact, course or chapter');
            return;
        }
        if (formData.quiz_category === 'COURSE' && mappings.length !== 1) {
            toast.error('A Course quiz must be mapped to exactly one course or chapter');
            return;
        }
        if (formData.quiz_category === 'ARTIFACT' && mappings.some(mapping => mapping.entity_type !== 'ARTIFACT')) {
            toast.error('An Artifact quiz can only be mapped to artifacts');
            return;
        }

        const questionsInScope = questions.filter(question =>
            (question.question || '').trim() ||
            (question.options || []).some(option => (option || '').trim())
        );

        for (let index = 0; index < questionsInScope.length; index++) {
            const question = questionsInScope[index];
            const positionInLevel = questions
                .filter(item => item.level_number === question.level_number)
                .findIndex(item => item.key === question.key);
            const error = validateQuestion(question, questionLabel(question, positionInLevel));
            if (error) {
                toast.error(error);
                return;
            }
        }

        if (isEdit) {
            const unsaved = questionsInScope.filter(question => question.dirty);
            if (unsaved.length > 0) {
                toast.error('Please save each edited question before saving the quiz');
                return;
            }
        }

        try {
            setIsSubmitting(true);

            let thumbnailUrl = formData.thumbnail_url;
            if (thumbnailFile) {
                thumbnailUrl = await uploadImage(
                    thumbnailFile,
                    `quiz_thumbnail_${Date.now()}`,
                    'thumbnails',
                    'images'
                );
            }

            if (isEdit) {
                const payload = {
                    ...buildDetailsPayload(thumbnailUrl),
                    // quiz_category must accompany mappings so the server can
                    // apply the placement rules.
                    mappings
                };
                const response = await _axios.put(`${ServerPath}/quiz-admin/${quizId}`, payload);
                if (response?.data?.success) {
                    toast.success('Quiz updated successfully');
                    onClose(true);
                } else {
                    toast.error(response?.data?.message || 'Failed to update quiz');
                }
            } else {
                const payload = {
                    ...buildDetailsPayload(thumbnailUrl),
                    created_by: currentId ? Number(currentId) : null,
                    mappings,
                    questions: questionsInScope.map(buildQuestionPayload)
                };
                const response = await _axios.post(`${ServerPath}/quiz-admin`, payload);
                if (response?.data?.success) {
                    toast.success('Quiz created successfully');
                    onClose(true);
                } else {
                    toast.error(response?.data?.message || 'Failed to create quiz');
                }
            }
        } catch (error) {
            console.error('Error saving quiz:', error);
            const message = getApiErrorMessage(error, 'Failed to save quiz');
            if (isConflictError(error)) {
                // 409 always points at the mapping: that entity already has a quiz.
                setMappingError(message);
            }
            if (isNotFoundError(error) && isEdit) {
                toast.error('This quiz no longer exists');
                onClose(true);
                return;
            }
            toast.error(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    /* -------------------------------------------------------------- *
     * Render helpers
     * -------------------------------------------------------------- */

    const renderTextRow = (labelPrefix, fieldPrefix, required, isTextarea) => (
        <div className="row mb-3">
            {QUIZ_LANGUAGES.map(language => {
                const field = `${fieldPrefix}${language.suffix}`;
                const label = language.suffix ? `${labelPrefix} ${language.short}` : labelPrefix;
                return (
                    <div key={language.key} className="col-3">
                        <Form.Group>
                            <Form.Label className="mb-2" style={labelStyle}>
                                {label} {required && !language.suffix && <span className="text-danger">*</span>}
                            </Form.Label>
                            <Form.Control
                                as={isTextarea ? 'textarea' : 'input'}
                                rows={isTextarea ? 3 : undefined}
                                type={isTextarea ? undefined : 'text'}
                                placeholder={`Enter ${label.toLowerCase()}`}
                                value={formData[field]}
                                onChange={(event) => handleFieldChange(field, event.target.value)}
                                style={isTextarea ? controlStyle : { ...controlStyle, height: '44px' }}
                            />
                        </Form.Group>
                    </div>
                );
            })}
        </div>
    );

    const filteredArtifacts = artifactOptions.filter(artifact => {
        if (selectedArtifacts.some(selected => String(selected.artifact_id) === String(artifact.artifact_id))) {
            return false;
        }
        if (!artifactSearch.trim()) return true;
        return (artifact.title || '').toLowerCase().includes(artifactSearch.toLowerCase());
    });

    const renderArtifactSection = () => (
        <div className="row mb-3">
            <div className="col-3">
                <Form.Group>
                    <Form.Label className="mb-2" style={labelStyle}>
                        Artifact Type <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                        value={formData.artifact_type}
                        onChange={(event) => handleArtifactTypeChange(event.target.value)}
                        style={selectStyle}
                    >
                        <option value="" hidden>Select Artifact Type</option>
                        {ARTIFACT_TYPE_OPTIONS.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </Form.Select>
                </Form.Group>
            </div>

            <div className="col-4">
                <Form.Group>
                    <Form.Label className="mb-2" style={labelStyle}>
                        Select Artifact for quiz <span className="text-danger">*</span>
                    </Form.Label>
                    <div className="position-relative">
                        <Form.Control
                            type="text"
                            placeholder={formData.artifact_type ? 'Search artifact' : 'Select an artifact type first'}
                            value={artifactSearch}
                            disabled={!formData.artifact_type}
                            onChange={(event) => setArtifactSearch(event.target.value)}
                            style={{ ...controlStyle, height: '44px', paddingRight: '35px' }}
                        />
                        <img
                            src={Search}
                            alt="search"
                            className="position-absolute"
                            style={{ right: '10px', top: '50%', transform: 'translateY(-50%)', width: '16px', height: '16px', zIndex: 2 }}
                        />
                        {artifactSearch.length > 0 && (
                            <div
                                className="position-absolute w-100 bg-white border rounded mt-1 shadow-sm"
                                style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}
                            >
                                {filteredArtifacts.length === 0 && (
                                    <div className="p-2 text-muted" style={{ fontSize: '13px' }}>No artifacts found</div>
                                )}
                                {filteredArtifacts.map(artifact => (
                                    <div
                                        key={artifact.artifact_id}
                                        className="p-2"
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => {
                                            setSelectedArtifacts(prev => [...prev, artifact]);
                                            setArtifactSearch('');
                                            setMappingError('');
                                        }}
                                    >
                                        {artifact.title}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </Form.Group>
            </div>

            <div className="col-5">
                <Form.Group>
                    <Form.Label className="mb-2" style={labelStyle}>Selected</Form.Label>
                    <div
                        style={{
                            minHeight: '110px',
                            border: '1px solid #E2E8F0',
                            borderRadius: '0.375rem',
                            padding: '8px 12px',
                            backgroundColor: '#F1F1F1'
                        }}
                    >
                        {selectedArtifacts.length === 0 && (
                            <span className="text-muted" style={{ fontSize: '13px' }}>
                                No artifact selected yet
                            </span>
                        )}
                        {selectedArtifacts.map(artifact => (
                            <span
                                key={artifact.artifact_id}
                                className="d-inline-flex align-items-center me-2 mb-2"
                                style={{ backgroundColor: '#6366F1', color: 'white', padding: '4px 10px', borderRadius: '6px', fontSize: '13px' }}
                            >
                                {artifact.title}
                                <button
                                    type="button"
                                    className="ms-2 border-0 bg-transparent text-white"
                                    style={{ cursor: 'pointer', fontSize: '16px', lineHeight: 1 }}
                                    onClick={() => {
                                        setSelectedArtifacts(prev => prev.filter(item =>
                                            String(item.artifact_id) !== String(artifact.artifact_id)
                                        ));
                                        setMappingError('');
                                    }}
                                >
                                    ×
                                </button>
                            </span>
                        ))}
                    </div>
                </Form.Group>
            </div>
        </div>
    );

    const renderCourseSection = () => (
        <div className="row mb-3">
            <div className="col-4">
                <Form.Group>
                    <Form.Label className="mb-2" style={labelStyle}>
                        Select Course <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                        value={selectedCourseId}
                        onChange={(event) => handleCourseChange(event.target.value)}
                        style={selectStyle}
                    >
                        <option value="" hidden>Select Course</option>
                        {courses.map(course => (
                            <option key={course.course_id} value={course.course_id}>{course.title}</option>
                        ))}
                    </Form.Select>
                </Form.Group>
            </div>
            <div className="col-4">
                <Form.Group>
                    <Form.Label className="mb-2" style={labelStyle}>Select Chapter (Optional)</Form.Label>
                    <Form.Select
                        value={selectedChapterId}
                        disabled={!selectedCourseId}
                        onChange={(event) => {
                            setSelectedChapterId(event.target.value);
                            setMappingError('');
                        }}
                        style={selectStyle}
                    >
                        <option value="">No chapter - Final Assessment</option>
                        {chapters.map(chapter => (
                            <option key={chapter.chapter_id} value={chapter.chapter_id}>{chapter.title}</option>
                        ))}
                    </Form.Select>
                    <div className="text-muted mt-1" style={{ fontSize: '12px' }}>
                        Leave chapter empty to show this quiz as the course's Final Assessment.
                    </div>
                </Form.Group>
            </div>
        </div>
    );

    const renderQuestionCard = (question, index) => {
        const isSaving = savingQuestionKey === question.key;

        return (
            <div
                key={question.key}
                draggable={draggableKey === question.key}
                onDragStart={() => handleDragStart(question.key)}
                onDragOver={handleDragOver}
                onDrop={() => handleDrop(question.key)}
                onDragEnd={() => setDraggableKey(null)}
                className="border rounded p-3 mb-3"
                style={{ backgroundColor: '#FFFFFF', borderColor: '#E2E8F0' }}
            >
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <div className="d-flex align-items-center">
                        <span
                            title="Drag to reorder"
                            onMouseDown={() => setDraggableKey(question.key)}
                            onMouseUp={() => setDraggableKey(null)}
                            style={{ cursor: 'grab', color: '#A0AEC0', marginRight: '10px', fontSize: '18px', lineHeight: 1 }}
                        >
                            ⣿
                        </span>
                        <span style={{ ...labelStyle, fontSize: '15px' }}>
                            Question {index + 1}
                            {question.dirty && (
                                <span className="ms-2 text-warning" style={{ fontSize: '12px' }}>• unsaved</span>
                            )}
                        </span>
                    </div>
                    <Button
                        variant="link"
                        className="p-0 text-danger"
                        style={{ textDecoration: 'none', fontSize: '13px' }}
                        onClick={() => handleDeleteQuestion(question, index)}
                    >
                        Delete
                    </Button>
                </div>

                <div className="row">
                    {QUIZ_LANGUAGES.map(language => {
                        const questionField = `question${language.suffix}`;
                        const optionsField = `options${language.suffix}`;
                        const isEnglish = !language.suffix;

                        return (
                            <div key={language.key} className="col-3">
                                <Form.Group className="mb-2">
                                    <Form.Label className="mb-2" style={{ ...labelStyle, fontSize: '13px' }}>
                                        {isEnglish ? `Question ${index + 1}` : `Question ${language.short} ${index + 1}`}
                                        {isEnglish && <span className="text-danger"> *</span>}
                                    </Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        placeholder={isEnglish ? 'Enter the question' : 'Same as Eng'}
                                        value={question[questionField]}
                                        onChange={(event) => updateQuestion(question.key, { [questionField]: event.target.value })}
                                        style={controlStyle}
                                    />
                                </Form.Group>

                                <Form.Label
                                    className="mb-2 d-block"
                                    style={{ ...labelStyle, fontSize: '13px' }}
                                    title="Selecting the correct answer in any language sets it for all of them"
                                >
                                    Options {isEnglish && <span className="text-danger">*</span>}
                                    <span className="text-muted ms-1" style={{ fontSize: '11px', fontWeight: '400' }}>
                                        (select the correct answer)
                                    </span>
                                </Form.Label>
                                {OPTION_LETTERS.map((letter, optionIndex) => (
                                    <div key={letter} className="d-flex align-items-center mb-2">
                                        <span className="me-2" style={{ color: '#4A5568', fontSize: '13px', width: '18px' }}>
                                            {letter})
                                        </span>
                                        <Form.Control
                                            type="text"
                                            placeholder={isEnglish ? `Option ${letter}` : 'Same as Eng'}
                                            value={question[optionsField][optionIndex]}
                                            onChange={(event) => updateQuestionOption(question.key, language.suffix, optionIndex, event.target.value)}
                                            style={{ ...controlStyle, height: '38px' }}
                                        />
                                        {/* The correct answer is the position (A-D), so it is the
                                            same choice in every language - each column gets its own
                                            radio group but they all drive correct_option. */}
                                        <Form.Check
                                            type="radio"
                                            className="ms-2"
                                            name={`correct_${question.key}_${language.key}`}
                                            title={`Mark ${letter} as the correct answer`}
                                            checked={question.correct_option === letter}
                                            onChange={() => updateQuestion(question.key, { correct_option: letter })}
                                        />
                                    </div>
                                ))}
                            </div>
                        );
                    })}
                </div>

                <div className="d-flex justify-content-end">
                    <Button
                        variant="primary"
                        size="sm"
                        style={{ backgroundColor: '#6366F1', minWidth: '80px' }}
                        disabled={isSaving}
                        onClick={() => handleSaveQuestion(question, index)}
                    >
                        {isSaving ? 'Saving...' : 'Save'}
                    </Button>
                </div>
            </div>
        );
    };

    /* -------------------------------------------------------------- *
     * Render
     * -------------------------------------------------------------- */

    if (loading) {
        return (
            <Modal.Body className="px-4 py-5 text-center">
                <Spinner animation="border" variant="primary" />
                <div className="mt-2 text-muted">Loading quiz...</div>
            </Modal.Body>
        );
    }

    return (
        <>
            <Modal.Body
                className="px-4 py-3"
                style={{
                    maxHeight: 'calc(90vh - 160px)',
                    overflowY: 'auto',
                    overflowX: 'hidden'
                }}
            >
                <Form onSubmit={(event) => event.preventDefault()}>
                    {/* Quiz Details */}
                    <div className="mb-4">
                        <h6 className="text-primary mb-4" style={{ fontSize: '16px', color: '#6366F1' }}>Quiz Details</h6>

                        {renderTextRow('Quiz Title', 'title', true, false)}
                        {renderTextRow('Description', 'description', false, true)}

                        <div className="row mb-3">
                            <div className="col-3">
                                <Form.Group>
                                    <Form.Label className="mb-2" style={labelStyle}>Thumbnail</Form.Label>
                                    <input
                                        type="file"
                                        id="quizThumbnailFile"
                                        accept="image/png,image/jpeg,image/jpg,image/webp"
                                        onChange={handleThumbnailChange}
                                        style={{ display: 'none' }}
                                    />
                                    <div
                                        className="border border-2 border-dashed rounded position-relative d-flex align-items-center justify-content-center"
                                        style={{ height: '110px', backgroundColor: '#FFFFFF', cursor: 'pointer' }}
                                        onClick={() => document.getElementById('quizThumbnailFile').click()}
                                    >
                                        {thumbnailPreview ? (
                                            <>
                                                <img
                                                    src={thumbnailPreview}
                                                    alt="thumbnail"
                                                    style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
                                                />
                                                <span
                                                    title="Remove thumbnail"
                                                    onClick={handleThumbnailRemove}
                                                    style={{
                                                        position: 'absolute',
                                                        top: '4px',
                                                        right: '6px',
                                                        width: '22px',
                                                        height: '22px',
                                                        borderRadius: '50%',
                                                        backgroundColor: 'rgba(0,0,0,0.55)',
                                                        color: '#FFFFFF',
                                                        textAlign: 'center',
                                                        lineHeight: '22px',
                                                        cursor: 'pointer',
                                                        zIndex: 2
                                                    }}
                                                >
                                                    ×
                                                </span>
                                            </>
                                        ) : (
                                            <div className="d-flex flex-column align-items-center justify-content-center text-center px-2">
                                                <span style={{ fontSize: '28px', color: '#A0AEC0', lineHeight: 1 }}>+</span>
                                                <p style={{ fontSize: '10px', color: '#718096', margin: '4px 0 0' }}>
                                                    Please ensure the image is in JPG or PNG format.
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </Form.Group>
                            </div>

                            <div className="col-3">
                                <Form.Group>
                                    <Form.Label className="mb-2" style={labelStyle}>
                                        Quiz Category <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Select
                                        value={formData.quiz_category}
                                        onChange={(event) => handleCategoryChange(event.target.value)}
                                        style={selectStyle}
                                    >
                                        <option value="" hidden>Select Category</option>
                                        {QUIZ_CATEGORY_OPTIONS.map(option => (
                                            <option key={option.value} value={option.value}>{option.label}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </div>

                            <div className="col-3">
                                <Form.Group>
                                    <Form.Label className="mb-2" style={labelStyle}>
                                        Topic
                                        {isQuizSection && formData.status === 'ACTIVE' && <span className="text-danger"> *</span>}
                                    </Form.Label>
                                    <Form.Select
                                        value={formData.topic}
                                        onChange={(event) => handleFieldChange('topic', event.target.value)}
                                        style={selectStyle}
                                    >
                                        <option value="">Select Topic</option>
                                        {TOPIC_OPTIONS.map(option => (
                                            <option key={option.value} value={option.value}>{option.label}</option>
                                        ))}
                                    </Form.Select>
                                    {isQuizSection && (
                                        <div className="text-muted mt-1" style={{ fontSize: '12px' }}>
                                            Required before a Quiz Section quiz can be set to Active.
                                        </div>
                                    )}
                                </Form.Group>
                            </div>

                            <div className="col-3">
                                <Form.Group>
                                    <Form.Label className="mb-2" style={labelStyle}>Status</Form.Label>
                                    <Form.Select
                                        value={formData.status}
                                        onChange={(event) => handleFieldChange('status', event.target.value)}
                                        style={selectStyle}
                                    >
                                        {STATUS_OPTIONS.map(option => (
                                            <option key={option.value} value={option.value}>{option.label}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </div>
                        </div>

                        {/* Placement - driven by the selected category */}
                        {formData.quiz_category === 'ARTIFACT' && renderArtifactSection()}
                        {formData.quiz_category === 'COURSE' && renderCourseSection()}

                        {mappingError && (
                            <div className="alert alert-danger py-2 px-3" style={{ fontSize: '13px' }}>
                                {mappingError}
                            </div>
                        )}

                        {/* Timer and shuffle */}
                        <div className="row mb-2 align-items-end">
                            <div className="col-3">
                                <Form.Check
                                    type="checkbox"
                                    id="quizTimerEnabled"
                                    label="Enable Timer"
                                    checked={!!formData.timer_enabled}
                                    onChange={(event) => {
                                        const enabled = event.target.checked ? 1 : 0;
                                        setFormData(prev => ({
                                            ...prev,
                                            timer_enabled: enabled,
                                            time_per_question_seconds: enabled ? prev.time_per_question_seconds : ''
                                        }));
                                    }}
                                />
                            </div>
                            {!!formData.timer_enabled && (
                                <div className="col-3">
                                    <Form.Group>
                                        <Form.Label className="mb-2" style={labelStyle}>
                                            Time per Question <span className="text-danger">*</span>
                                        </Form.Label>
                                        <Form.Select
                                            value={formData.time_per_question_seconds}
                                            onChange={(event) => handleFieldChange('time_per_question_seconds', event.target.value)}
                                            style={selectStyle}
                                        >
                                            <option value="" hidden>Select Time</option>
                                            {TIMER_OPTIONS.map(option => (
                                                <option key={option.value} value={option.value}>{option.label}</option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </div>
                            )}
                            <div className="col-3">
                                <Form.Check
                                    type="checkbox"
                                    id="quizShuffleQuestions"
                                    label="Shuffle Questions"
                                    checked={!!formData.shuffle_questions}
                                    onChange={(event) => handleFieldChange('shuffle_questions', event.target.checked ? 1 : 0)}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Question Management */}
                    <div className="mb-3">
                        <h6 className="text-primary mb-3" style={{ fontSize: '16px', color: '#6366F1' }}>Question Management</h6>

                        {isQuizSection && (
                            <div className="mb-3">
                                <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
                                    {visibleLevels.map(level => (
                                        <Button
                                            key={level}
                                            variant={activeLevel === level ? 'primary' : 'outline-secondary'}
                                            size="sm"
                                            style={activeLevel === level ? { backgroundColor: '#6366F1', borderColor: '#6366F1' } : {}}
                                            onClick={() => setActiveLevel(level)}
                                        >
                                            Level {level}
                                        </Button>
                                    ))}
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        disabled={visibleLevels.length >= MAX_LEVELS}
                                        onClick={handleAddLevel}
                                    >
                                        + Add Level
                                    </Button>
                                </div>
                                <div className="text-muted" style={{ fontSize: '12px' }}>
                                    A level only exists through its questions - a level left without any saved
                                    question will not be kept. Maximum {MAX_LEVELS} levels.
                                </div>
                            </div>
                        )}

                        {levelQuestions.length === 0 && (
                            <div className="text-muted mb-3" style={{ fontSize: '13px' }}>
                                No questions yet{isQuizSection ? ` for Level ${activeLevel}` : ''}. Add one below.
                            </div>
                        )}

                        {levelQuestions.map((question, index) => renderQuestionCard(question, index))}

                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={handleAddQuestion}
                        >
                            + Add Question
                        </Button>
                    </div>
                </Form>
            </Modal.Body>

            <Modal.Footer style={{ borderTop: '1px solid #E2E8F0', justifyContent: 'flex-end' }}>
                <Button
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    style={{ backgroundColor: '#6366F1', minWidth: '100px' }}
                >
                    {isSubmitting ? 'Saving...' : (isEdit ? 'Save' : 'Create')}
                </Button>
            </Modal.Footer>
        </>
    );
};

export default QuizForm;
